/**
 * TERMS AND PRIVACY (spec §86-14).
 *
 * The documents live in ONE place, as data, with a version. The public
 * pages render them, the server records which VERSION somebody accepted,
 * and publishing a revision is an edit here plus a new version string —
 * the session then asks each person to accept the new text once. No other
 * code changes.
 *
 * These texts were written to describe what Berrier Reno actually does.
 * They have NOT been reviewed by an attorney; the owner is responsible for
 * that review before relying on them commercially.
 */

export interface LegalSection {
  heading: string;
  paragraphs: string[];
}

export interface LegalDocument {
  id: "terms" | "privacy";
  title: string;
  version: string;
  effectiveDate: string;
  summary: string;
  sections: LegalSection[];
}

export const TERMS: LegalDocument = {
  id: "terms",
  title: "Terms of Service",
  version: "2026-09-17",
  effectiveDate: "September 17, 2026",
  summary:
    "Berrier Reno is planning software. Its estimates help you plan; they are not quotes, appraisals, or financial advice, and decisions about your property remain yours.",
  sections: [
    {
      heading: "1. Who we are",
      paragraphs: [
        "Berrier Reno is software for planning, estimating and tracking renovation and construction projects, provided by Berrier Holdings LLC (\"we\", \"us\"). These terms govern your use of it.",
      ],
    },
    {
      heading: "2. Early Access",
      paragraphs: [
        "Berrier Reno is offered as Early Access software. Features, screens and calculations may change, and some parts may be incomplete or contain errors. We will work to fix problems you report, but we do not promise that any feature will be available at any particular time.",
      ],
    },
    {
      heading: "3. Estimates are planning tools",
      paragraphs: [
        "Estimates, budgets, quantities, schedules, forecasts, financing projections, after-repair values and profit figures in Berrier Reno are planning aids calculated from the information you enter and from general cost assumptions. They are not quotes, bids, appraisals, inspections, engineering, legal, tax, lending or investment advice.",
        "Actual costs depend on your property, market, contractors, materials, permits and many conditions the software cannot know. Berrier Reno does not guarantee any project cost, schedule, property value, sale price, rent, financing approval, return or profit.",
        "You are responsible for verifying prices and scope with licensed contractors and suppliers, confirming building-code and permit requirements with the appropriate authorities, and for every decision you make about buying, financing, renovating, renting or selling a property.",
      ],
    },
    {
      heading: "4. Your account",
      paragraphs: [
        "Keep your password private and tell us promptly if you believe someone else has used your account. You are responsible for activity under your account. One person per account.",
      ],
    },
    {
      heading: "5. Your data",
      paragraphs: [
        "Your projects, designs, estimates, receipts and other content belong to you. You give us permission to store and process that content only to operate Berrier Reno for you. We do not sell your content. Our Privacy Policy explains what we collect and why.",
        "If you share a project with another person, they can see what that sharing allows. You are responsible for choosing who you share with.",
      ],
    },
    {
      heading: "6. Subscription and payment",
      paragraphs: [
        "Paid Early Access is billed in advance on a recurring basis through our payment processor, Stripe, at the price shown when you subscribe. Your subscription renews automatically until you cancel. You can cancel at any time from Billing; cancellation takes effect at the end of the current billing period, and you keep access until then.",
        "If a payment fails, we may retry it. If payment cannot be collected, access to the application may stop. Payments already made are not refunded except where required by law or where we choose to.",
        "We may change the price for future billing periods. We will tell you before a change affects you, and you can cancel before it does.",
      ],
    },
    {
      heading: "7. When access ends",
      paragraphs: [
        "If your subscription ends or your account is closed, we keep your projects so you can return to them, unless you ask us to delete your account. Losing access never deletes your data by itself.",
        "We may suspend accounts that are used to break the law, attack the service, or access other people's data.",
      ],
    },
    {
      heading: "8. Acceptable use",
      paragraphs: [
        "Do not attempt to access other accounts or data that is not yours, interfere with the service, reverse-engineer it to build a competing product, or use it to store unlawful content.",
      ],
    },
    {
      heading: "9. Disclaimers and limits",
      paragraphs: [
        "Berrier Reno is provided \"as is\" and \"as available\". To the fullest extent permitted by law, we disclaim warranties of merchantability, fitness for a particular purpose and non-infringement, and we are not liable for indirect, incidental, special, consequential or lost-profit damages, or for losses arising from reliance on estimates or projections.",
        "To the fullest extent permitted by law, our total liability for any claim relating to Berrier Reno is limited to the amount you paid us in the three months before the claim.",
      ],
    },
    {
      heading: "10. Changes to these terms",
      paragraphs: [
        "We may update these terms. When we do, we will change the version date above and ask you to accept the new version before you continue using Berrier Reno.",
      ],
    },
    {
      heading: "11. Contact",
      paragraphs: ["Questions about these terms: use Send feedback inside the application, or contact Berrier Holdings LLC."],
    },
  ],
};

export const PRIVACY: LegalDocument = {
  id: "privacy",
  title: "Privacy Policy",
  version: "2026-09-17",
  effectiveDate: "September 17, 2026",
  summary:
    "Your projects are yours. We store them to run Berrier Reno, we record which features are used so we can improve it, and we never sell your data.",
  sections: [
    {
      heading: "What we store",
      paragraphs: [
        "Account information: your email address, name, optional company name, the answers you give when you first sign in, your password as a one-way hash (never the password itself), and the dates you signed up and last used the app.",
        "Your content: the projects, properties, designs, estimates, schedules, financing terms, receipts, documents and notes you create. Each account's projects are kept in a separate database.",
        "Billing: your subscription status and Stripe customer reference. Card numbers are entered on Stripe's pages and are never sent to or stored by us.",
      ],
    },
    {
      heading: "What we record about how Berrier Reno is used",
      paragraphs: [
        "Named product events (for example: a project was created, an estimate was reached, a report was exported), with the date and a few small non-identifying values. These never include your project contents, addresses, prices or free text.",
        "Errors, with technical detail stripped of passwords, keys and tokens, so we can fix them.",
        "Feedback you choose to send, with the screen and project you were on.",
        "We do not record your screen, keystrokes or mouse movements, and we do not use advertising trackers.",
      ],
    },
    {
      heading: "Services that process data for us",
      paragraphs: [
        "Stripe (payments), Resend (account and billing emails), Fly.io (hosting), Cloudflare R2 (encrypted off-site backups). When you ask Berrier Reno to read a receipt or loan document, that file is sent to Microsoft Azure Document Intelligence to read it; when you search for products, the search terms are sent to a product-search provider. Each receives only what that feature needs.",
      ],
    },
    {
      heading: "What we never do",
      paragraphs: [
        "We do not sell or rent your data, and we do not use your project contents to train artificial intelligence models.",
      ],
    },
    {
      heading: "Sharing",
      paragraphs: [
        "A project you share can be seen by the people you share it with, within the permission you chose. Administrators of Berrier Reno can see account-level information (email, sign-up date, subscription state, which setup milestones were reached, and feedback) to operate the service.",
      ],
    },
    {
      heading: "Retention and deletion",
      paragraphs: [
        "We keep your data while your account exists, including after a subscription ends, so you can return to it. Backups are kept for up to 30 days. You can ask us to delete your account and its projects; deletion is permanent once backups age out.",
      ],
    },
    {
      heading: "Security",
      paragraphs: [
        "Connections are encrypted, passwords are hashed with scrypt, each account's projects are stored separately, and access is checked on the server for every request.",
      ],
    },
    {
      heading: "Changes and contact",
      paragraphs: [
        "If this policy changes, we will update the version date and ask you to review it. Questions: use Send feedback inside the application, or contact Berrier Holdings LLC.",
      ],
    },
  ],
};

export const LEGAL_DOCUMENTS = { terms: TERMS, privacy: PRIVACY } as const;
